import { Typography } from '@mui/material';
import { IFormFieldConfig } from '@shared/components/FormFieldsConstructor';
import { FieldCompareStatus, TextInputField } from '@shared/fields/TextInputField';
import { formatDate } from '@shared/commonFunctions';
import { dateFormat, dateFormatWithTime, ocrDateFormat } from '@shared/constants/formats';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { SelectDateField } from '@shared/fields/SelectDateField';
import { STIP_TYPE_ADD123_CARFAX, STIP_TYPE_REGISTRATION } from '@shared/constants/constants';
import { muiTheme } from '@styles/muiTheme';
import { Normalizable } from '@shared/utils/helpers';
import { NContact } from '../../Contact/validation';
import { ICoApplicant } from '../../Contact/components/CoApplicant/validation';
import {
  CO_BORROWER_DRIVER_LICENSE_BACK_STIPS_TYPE,
  CO_BORROWER_DRIVER_LICENSE_FRONT_STIPS_TYPE,
  PROOF_OF_INCOME_STIPS_TYPE,
  PROOF_OF_INCOME_SECONDARY_STIPS_TYPE,
  PROOF_OF_INCOME_W2_STIPS_TYPE,
  PROOF_OF_RESIDENCY_STIPS_TYPE,  
  DRIVER_LICENSE_FRONT_STIPS_TYPE,
  DRIVER_LICENSE_BACK_STIPS_TYPE,
  IStip,
  IContactFile,
  TITLE_STIPS_TYPE,
  ODOMETER_STIPS_TYPE,
  INSURANCE_STIPS_TYPE,
  BOOKOUT_STIPS_TYPE,
  APPROVAL_STIPS_TYPE,
  REFERENCES_STIPS_TYPE,
} from '../interfaces';
import { IAsset, VALUATION_TYPE_OPTIONS } from '../../Assets/interfaces';
import { getStatus } from '../../../shared';

export const getFieldCompareStatus = (
  fieldPath?: string,
  compareWith?: IFormFieldConfig['compareWith'],
  values?: IStip,
  contactData?: NContact.Entity,
  assetData?: IAsset,
  coApplicant?: ICoApplicant
): FieldCompareStatus => {
  if (!fieldPath || !compareWith) return 'none';

   const formValue = normalizeValue(getValueByPath(values, fieldPath) as Normalizable);

  let sourceValue: Normalizable = null;

  if (compareWith.contactResolver && contactData) {
    if (
      (fieldPath?.includes('ownerName') || fieldPath?.includes('fullName') || fieldPath?.includes('employeeName')) &&
      contactData
    ) {
      sourceValue = doesNameMatch(formValue, getContactFullName(contactData));
    } else if (fieldPath.includes('address.street') && contactData) {
      sourceValue = doesOcrStreetMatchCurrentContact(formValue as string, contactData);
    } else if (fieldPath.includes('employerAddress.zipCode')) {
      const currentEmployment = contactData?.employments.find((e) => e.isCurrent);
      sourceValue = doesZipCodeMatch(formValue, currentEmployment);
    } else if (fieldPath.includes('.zipCode')) {
      const currentAddress = contactData?.addresses?.find((a) => a.isCurrent);
      sourceValue = doesZipCodeMatch(formValue, currentAddress);
    } else {
      sourceValue = compareWith.contactResolver(contactData);
    }
  } else if (compareWith.contactPath && contactData) {
    sourceValue = getValueByPath(contactData, compareWith.contactPath) as Normalizable;
  }

  if (compareWith.assetResolver && assetData) {
    if (fieldPath.includes('.vin') && assetData) {
      sourceValue = doesAnyOcrVinMatchAssetVin(formValue as string, assetData);
    } else {
      sourceValue = compareWith.assetResolver(assetData);
    }
  } else if (compareWith.assetPath && assetData) {
    if (fieldPath?.endsWith('.make')) {
      sourceValue = doesMakeMatch(formValue, assetData);
    } else if (fieldPath?.includes('ownerName')) {
      sourceValue = doesNameMatch(formValue, assetData?.assetRegistration?.primaryOwner);
    } else if (fieldPath?.includes('policyNumber')) {
      const formPolicyNumber = normalizePolicyNumber(formValue);
      const sourcePolicyNumber = normalizePolicyNumber(assetData?.assetInsurance?.policyNumber);
      sourceValue = !!formPolicyNumber && !!sourcePolicyNumber && formPolicyNumber === sourcePolicyNumber;
    } else if (fieldPath?.includes('lienholderName')) {
      sourceValue = doesNameMatch(formValue, assetData?.assetLienHolder?.name);
    } else {
      sourceValue = getValueByPath(assetData, compareWith.assetPath) as Normalizable;
    }
  }

  if (compareWith.coApplicantResolver && coApplicant) {
    if (fieldPath?.includes('fullName') && coApplicant) {
      sourceValue = doesNameMatch(formValue, getContactFullName(coApplicant));
    } else if (fieldPath.includes('address.street') && coApplicant) {
      sourceValue = doesOcrStreetMatchCurrentContact(formValue as string, coApplicant);
    } else if (fieldPath.includes('.zipCode')) {
      const currentAddress = coApplicant?.addresses?.find((a) => a.isCurrent);
      sourceValue = doesZipCodeMatch(formValue, currentAddress);
    } else {
      sourceValue = compareWith.coApplicantResolver(coApplicant);
    }
  } else if (compareWith.coApplicantPath && coApplicant) {
    sourceValue = getValueByPath(coApplicant, compareWith.coApplicantPath) as Normalizable;
  }

  //boolean result for street match and VIN match
  if (typeof sourceValue === 'boolean') {
    return sourceValue ? 'matched' : 'mismatched';
  }

  if (formValue === '') return 'none';

  return formValue === normalizeValue(sourceValue) ? 'matched' : 'mismatched';
};

export const normalizeValue = (value: Normalizable): string => {
  if (value == null) return '';

  // Date object
  if (value instanceof Date && !isNaN(value.getTime())) {
    return value.toISOString().split('T')[0];
  }

  // String that represents a date (ANY format)
  if (typeof value === 'string' && isParsableDateString(value)) {
    return new Date(value).toISOString().split('T')[0];
  }

  //string
  if (typeof value === 'string') {
    return value.toLowerCase().replace(/\s+/g, ' ').replace(/[,.]/g, '');
  }

  return value.toString();
};

/* eslint-disable @typescript-eslint/no-explicit-any */
export const getValueByPath = (obj: any, path?: string) => path?.split('.').reduce((acc, key) => acc?.[key], obj);

export const isParsableDateString = (value: string): boolean => {
  const v = value.trim().toLowerCase();
  const monthNameToNumber: Record<string, number> = {
    jan: 1,
    feb: 2,
    mar: 3,
    apr: 4,
    may: 5,
    jun: 6,
    jul: 7,
    aug: 8,
    sep: 9,
    sept: 9,
    oct: 10,
    nov: 11,
    dec: 12,
  };

  const isValidDateParts = (year: number, month: number, day: number): boolean => {
    if (month < 1 || month > 12 || day < 1) return false;
    const maxDay = new Date(year, month, 0).getDate();
    return day <= maxDay;
  };

  if (/^\d+$/.test(v)) return false;

  // ISO / UTC
  const isoMatch = v.match(/^(\d{4})-(\d{2})-(\d{2})(?:[tT]\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?)?$/);
  if (isoMatch) {
    const year = Number(isoMatch[1]);
    const month = Number(isoMatch[2]);
    const day = Number(isoMatch[3]);
    if (!isValidDateParts(year, month, day)) return false;
    return !isNaN(new Date(v).getTime());
  }

  // MM/DD/YYYY or MM-DD-YYYY
  const usDateMatch = v.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
  if (usDateMatch) {
    const month = Number(usDateMatch[1]);
    const day = Number(usDateMatch[2]);
    const year = Number(usDateMatch[3]);
    return isValidDateParts(year, month, day);
  }

  // Month name formats (Nov 18 2023 / Nov 18, 2023)
  const monthNameMatch = v.match(/^(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\s+(\d{1,2}),?\s+(\d{4})$/i);
  if (monthNameMatch) {
    const month = monthNameToNumber[monthNameMatch[1]];
    const day = Number(monthNameMatch[2]);
    const year = Number(monthNameMatch[3]);
    return isValidDateParts(year, month, day);
  }

  return false;
};

export const doesNameMatch = (a?: Normalizable, b?: Normalizable): boolean => {
  const tokensA = normalizeNameTokens(a);
  const tokensB = normalizeNameTokens(b);

  if (!tokensA.length || !tokensB.length) return false;

  // Every token in A must match something in B
  return tokensA.every((tokenA) => tokensB.some((tokenB) => tokenMatches(tokenA, tokenB)));
};

export const normalizeZipCode = (value?: Normalizable): string => {
  if (!value) return '';

  // keep only digits
  const digits = value.toString().replace(/\D/g, '');

  // consider first 5 digits
  return digits.length >= 5 ? digits.slice(0, 5) : digits;
};

export const doesZipCodeMatch = (ocr?: string | null, contact?: NContact.Address | null | NContact.Employment): boolean => {
  const ocrZip = ocr;
  const contactZip = contact?.zip;

  if (!ocrZip || !contactZip) return false;

  //if both have extension of 4 digits or both do not have extension then compare directly else
  // normalize to 5 digits and compare
  if (ocrZip.length === contactZip.length) {
    return ocrZip === contactZip;
  }
  return normalizeZipCode(ocrZip) === normalizeZipCode(contactZip);
};

export const normalizePolicyNumber = (value?: Normalizable): string => {
  if (!value) return '';

  return value
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
};

const normalizeNameTokens = (value?: Normalizable): string[] => {
  if (!value) return [];

  return value.toString().toLowerCase().replace(/[.,]/g, '').replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
};

const tokenMatches = (a: string, b: string): boolean => {
  if (a === b) return true;

  // middle initial and full middle name comparison
  if (a.length === 1 && b.startsWith(a)) return true;
  if (b.length === 1 && a.startsWith(b)) return true;

  return false;
};

const doesOcrStreetMatchCurrentContact = (ocrStreetValue: string, contact?: NContact.Entity | ICoApplicant): boolean => {
  if (!contact?.addresses?.length || !ocrStreetValue) return false;

  const currentAddress = contact.addresses.find((a) => a.isCurrent);
  if (!currentAddress) return false;

  const ocrStreet = normalizeValue(ocrStreetValue);
  if (!ocrStreet) return false;

  const contactStreetLine1 = normalizeValue(currentAddress.streetLine1);

  const contactFullStreet = normalizeValue([currentAddress.streetLine1, currentAddress.streetLine2].filter(Boolean).join(' '));

  return (
    ocrStreet === contactStreetLine1 ||
    ocrStreet === contactFullStreet ||
    contactFullStreet.includes(ocrStreet) ||
    ocrStreet.includes(contactFullStreet)
  );
};

export const doesAnyOcrVinMatchAssetVin = (ocrVinValue?: string | null, asset?: IAsset | null): boolean => {
  if (!ocrVinValue || !asset?.vin) return false;

  const assetVin = asset.vin.toUpperCase().trim();

  const ocrVins = ocrVinValue
    .split(',')
    .map((v) => v.trim().toUpperCase())
    .filter(Boolean);

  return ocrVins.some((vin) => vin === assetVin);
};

export const doesMakeMatch = (ocrData?: string | null, asset?: IAsset | null): boolean => {
  const ocrMake = normalizeMake(ocrData);
  const assetMake = normalizeMake(asset?.make);
  if (!ocrMake || !assetMake) return false;

  if (ocrMake === assetMake) return true;

  if (assetMake.startsWith(ocrMake) || ocrMake.startsWith(assetMake)) {
    return true;
  }

  if (assetMake.includes(ocrMake) || ocrMake.includes(assetMake)) {
    return true;
  }

  return false;
};

export const normalizeMake = (value?: Normalizable): string => {
  if (!value) return '';

  return normalizeValue(value)
    .toLowerCase()
    .replace(/[^a-z]/g, '')
    .trim();
};

export const getContactFullName = (contact?: NContact.Entity | ICoApplicant) => {
  if (!contact) return undefined;

  const nameData = [
    normalizeValue(contact.lastName),
    normalizeValue(contact.firstName),
    normalizeValue(contact.middleInitial),
    normalizeValue(contact.suffix),
  ].filter(Boolean); // removes falsy values

  if (nameData.length === 0) return undefined;

  return nameData.join(' ');
};

const getEmployeeDetails =
  (field: keyof NContact.Employment) =>
  (contact?: NContact.Entity): Normalizable => {
    if (!contact?.employments?.length) return undefined;

    const currentEmployment = contact.employments.find((e) => e.isCurrent);
    return currentEmployment?.[field];
  };

export const getCurrentContactStreet = (contact?: NContact.Entity | ICoApplicant) => {
  if (!contact?.addresses?.length) return undefined;
  const current = contact.addresses.find((a) => a.isCurrent);

  if (!current) return undefined;
  return normalizeValue([current.streetLine1, current.streetLine2].filter(Boolean).join(' '));
};

export const getFields = ({ stipReview }: { stipReview?: IStip | undefined }): IFormFieldConfig<any>[] => {
  if (!stipReview) return [];
  const type = stipReview.stipTypeNumericId;
  switch (type) {
    case StipTypes.Approval:
      return approvalConfig(stipReview);
    case StipTypes.BookOut:
      return bookoutConfig(stipReview);
    case StipTypes.CarFax:
      return carFaxConfig(stipReview);
    case StipTypes.DlFront:
      return dlFrontConfigV2(stipReview);
    case StipTypes.DlFrontCb:
      return dlFrontCbConfigV2(stipReview);
    case StipTypes.DlBack:
      return dlBackConfig();
    case StipTypes.DlBackCb:
      return dlBackCbConfig();
    case StipTypes.Insurance:
    case StipTypes.InsuranceAlternate:
      return insuranceConfigV2();
    case StipTypes.Odometer:
      return odoMeterConfigV2();
    case StipTypes.Registration:
      return regConfigV2();
    case StipTypes.ProofOfIncome:
    case StipTypes.ProofOfIncomeSecondary:
    case StipTypes.ProofOfIncomeW2:
      return proofOfIncomeConfig();
    case StipTypes.ProofOfResidency:
      return proofOfResidencyConfig();
    case StipTypes.Title:
      return titleConfig(stipReview);
    case StipTypes.References:
      return referencesConfig(stipReview);
    default:
      return [];
  }
};

export const StipTypes = {
  Approval: 2,
  BookOut: 4,
  CarFax: 6,
  DlFront: 10,
  DlBack: 11,
  Insurance: 16,
  InsuranceAlternate: 90,
  Odometer: 18,
  Registration: 28,
  DlFrontCb: 44,
  DlBackCb: 45,
  ProofOfIncome: 22,
  ProofOfIncomeSecondary: 70,
  ProofOfIncomeW2: 121,
  ProofOfResidency: 25,
  Title: 31,
  References: 27
};

const contactFileStipTypeMap: Record<string, number> = {
  [APPROVAL_STIPS_TYPE]: StipTypes.Approval,
  [BOOKOUT_STIPS_TYPE]: StipTypes.BookOut,
  [STIP_TYPE_ADD123_CARFAX]: StipTypes.CarFax,
  [STIP_TYPE_REGISTRATION]: StipTypes.Registration,
  [DRIVER_LICENSE_FRONT_STIPS_TYPE]: StipTypes.DlFront,
  [DRIVER_LICENSE_BACK_STIPS_TYPE]: StipTypes.DlBack,
  [INSURANCE_STIPS_TYPE]: StipTypes.Insurance,
  [CO_BORROWER_DRIVER_LICENSE_FRONT_STIPS_TYPE]: StipTypes.DlFrontCb,
  [CO_BORROWER_DRIVER_LICENSE_BACK_STIPS_TYPE]: StipTypes.DlBackCb,
  [PROOF_OF_INCOME_STIPS_TYPE]: StipTypes.ProofOfIncome,
  [PROOF_OF_RESIDENCY_STIPS_TYPE]: StipTypes.ProofOfResidency,
  [PROOF_OF_INCOME_SECONDARY_STIPS_TYPE]: StipTypes.ProofOfIncomeSecondary,
  [PROOF_OF_INCOME_W2_STIPS_TYPE]: StipTypes.ProofOfIncomeW2,
  [TITLE_STIPS_TYPE]: StipTypes.Title,
  [ODOMETER_STIPS_TYPE]: StipTypes.Odometer,
  [REFERENCES_STIPS_TYPE]: StipTypes.References,
};

export const getStipTypeNumericIdForContactFile = (contactFile?: IContactFile): number | null => {
  if (!contactFile) return null;

  const numericId = contactFile.stipTypeNumericId ?? contactFileStipTypeMap[contactFile.stipType] ?? null;

  return numericId;
};

export const normalizeContactFileToStipReview = (contactFile?: IContactFile): IStip | null => {
  if (!contactFile?.ocrResult) return null;

  const stipTypeNumericId = getStipTypeNumericIdForContactFile(contactFile);

  if (!stipTypeNumericId) return null;

  const normalizedOcrResult = {
    ...contactFile,
    stipTypeNumericId,
  } as unknown as IStip;

  return normalizedOcrResult;
};

export const getComparableFieldsForContactFile = (stipReview?: IStip): IFormFieldConfig<any>[] => {
  if (!stipReview) return [];
  const fields = getFields({ stipReview });
  return fields;
};

const referencesConfig = (stipReview?: IStip) => {
  const references = stipReview?.ocrResult?.references?.references ?? [];

  return references.flatMap((_, index) => {
    const referencePath = `ocrResult.references.references[${index}]`;
    const referenceNumber = index + 1;

    return [
      {
        label: `Reference ${referenceNumber}`,
        component: Typography,
        variant: 'subtitle1',
      },
      {
        label: 'Name',
        name: `${referencePath}.name`,
        component: TextInputField,
        disabled: true,
      },
      {
        label: 'Contact Number',
        name: `${referencePath}.contactNumber`,
        component: TextInputField,
        disabled: true,
      },
      {
        label: 'Relationship',
        name: `${referencePath}.relation`,
        component: TextInputField,
        disabled: true,
      },
      {
        label: 'Street Address',
        name: `${referencePath}.address.street`,
        component: TextInputField,
        disabled: true,
      },
      {
        label: 'City',
        name: `${referencePath}.address.city`,
        component: TextInputField,
        disabled: true,
      },
      {
        label: 'State',
        name: `${referencePath}.address.state`,
        component: TextInputField,
        disabled: true,
      },
      {
        label: 'Zip Code',
        name: `${referencePath}.address.zipCode`,
        component: TextInputField,
        disabled: true,
      },
      {
        label: 'Email',
        name: `${referencePath}.email`,
        component: TextInputField,
        disabled: true,
      },
    ];
  });
};

const titleConfig = (stipReview?: IStip) => [
  {
    label: 'VIN',
    name: 'ocrResult.title.vin',
    component: TextInputField,
    disabled: true,
    compareWith: {
      assetPath: 'vin',
    },
  },
  {
    label: 'Owner Name',
    name: 'ocrResult.title.ownerName',
    component: TextInputField,
    disabled: true,
    compareWith: {
      assetPath: 'assetRegistration.primaryOwner',
    },
  },
  {
    label: 'Street Address',
    name: 'ocrResult.title.address.street',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: getCurrentContactStreet,
    },
  },
  {
    label: 'City',
    name: 'ocrResult.title.address.city',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: (contact: NContact.Entity) => {
        const current = contact.addresses?.find((a) => a.isCurrent);
        return current?.city;
      },
    },
  },
  {
    label: 'State',
    name: 'ocrResult.title.address.state',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: (contact: NContact.Entity) => {
        const current = contact.addresses?.find((a) => a.isCurrent);
        return current?.state;
      },
    },
  },
  {
    label: 'Zip Code',
    name: 'ocrResult.title.address.zipCode',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: (contact: NContact.Entity) => {
        const current = contact.addresses?.find((a) => a.isCurrent);
        return current?.zip;
      },
    },
  },
  {
    label: 'Issue Date',
    name: 'ocrResult.title.issueDate',
    component: TextInputField,
    disabled: true,
    children: formatDate(stipReview?.ocrResult?.title?.issueDate, { format: dateFormat }, ocrDateFormat),
    compareWith: {
      contactPath: 'driverLicenseIssuesOnDate',
    },
  },
  {
    label: 'LienholderName',
    name: 'ocrResult.title.lienholder',
    component: TextInputField,
    disabled: true,
    compareWith: {
      assetPath: 'assetLienHolder.name',
    },
  },
  {
    label: 'Odometer',
    name: 'ocrResult.title.odometerReading',
    component: TextInputField,
    disabled: true,
    compareWith: {
      assetPath: 'mileage',
    },
  },
];

const approvalConfig = (stipReview?: IStip) => [
  {
    label: 'Approval',
    component: Typography,
    variant: 'subtitle1',
  },
  {
    label: 'Application Number',
    name: 'appAplicationNumber',
    component: TextInputField,
  },
  {
    label: 'Approval Date',
    name: 'appApprovalDate',
    component: SelectDateField,
  },
  {
    label: 'Approval Amount',
    name: 'appApprovedAmount',
    prefix: '$',
    component: NumberInputField,
  },
  {
    label: 'Deal Info',
    component: Typography,
    variant: 'subtitle1',
  },
  {
    label: 'Contract Date',
    name: 'appContractDate',
    component: Typography,
    children: formatDate(stipReview?.appContractDate, { format: dateFormatWithTime }),
  },
  {
    label: 'Lender',
    name: 'appLender',
    component: Typography,
    children: stipReview?.appLender,
  },
  {
    label: 'Rate',
    name: 'appRate',
    component: Typography,
    children: stipReview?.appRate,
  },
  {
    label: 'Term',
    name: 'appTerm',
    component: Typography,
    children: stipReview?.appTerm,
  },
  {
    label: 'Front-End Amount',
    name: 'appFrontEndAmount',
    component: Typography,
    children: stipReview?.appFrontEndAmount,
  },
  {
    label: 'Total Amount Financed',
    name: 'appTotalAmountFinanced',
    component: Typography,
    children: stipReview?.appTotalAmountFinanced,
  },
  {
    label: 'Credit Score',
    name: 'appCreditScore',
    component: Typography,
    children: stipReview?.appCreditScore,
  },
  {
    label: 'LTV',
    name: 'appLTV',
    component: Typography,
    children: stipReview?.appLTV,
  },
  {
    label: 'Tier',
    name: 'appTier',
    component: Typography,
    children: stipReview?.appTier,
  },
  {
    label: 'Tier Jump',
    checkboxName: 'appTierJump',
    component: Typography,
    children: '',
    name: '',
  },
];

const carFaxConfig = (stipReview?: IStip) => [
  {
    label: 'Car Fax',
    component: Typography,
    variant: 'subtitle1',
  },
  {
    label: 'VIN Matches?',
    checkboxName: 'carFaxVinMatch',
    component: Typography,
    children: stipReview?.vinNumber,
    name: 'keyCarFaxVin',
  },
  {
    label: 'Mileage Less than or equal to?',
    checkboxName: 'carFaxMileageMatchs',
    component: Typography,
    children: stipReview?.mileage,
    name: 'keyCarFaxMileageMatchs',
  },
  {
    label: 'Clear Title',
    checkboxName: 'carFaxClearTitle',
    component: Typography,
    children: '',
    name: 'keyCarFaxClearTitle',
  },
];

const dlFrontConfigV2 = (stipReview?: IStip) => [
  {
    label: 'License Number',
    name: 'ocrResult.driverLicenseFront.licenseNumber',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactPath: 'driverLicenseNumber',
    },
  },
  {
    label: 'Full Name',
    name: 'ocrResult.driverLicenseFront.fullName',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: getContactFullName,
    },
  },
  {
    label: 'Date of Birth',
    name: 'ocrResult.driverLicenseFront.dob',
    component: TextInputField,
    disabled: true,
    children: formatDate(stipReview?.ocrResult?.driverLicenseFront?.dob, { format: dateFormat }, ocrDateFormat),
    compareWith: {
      contactPath: 'dateOfBirth',
    },
  },

  // Address Section
  {
    label: 'Street Address',
    name: 'ocrResult.driverLicenseFront.address.street',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: getCurrentContactStreet,
    },
  },
  {
    label: 'City',
    name: 'ocrResult.driverLicenseFront.address.city',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: (contact: NContact.Entity) => {
        const current = contact.addresses?.find((a) => a.isCurrent);
        return current?.city;
      },
    },
  },
  {
    label: 'State',
    name: 'ocrResult.driverLicenseFront.address.state',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: (contact: NContact.Entity) => {
        const current = contact.addresses?.find((a) => a.isCurrent);
        return current?.state;
      },
    },
  },
  {
    label: 'Zip Code',
    name: 'ocrResult.driverLicenseFront.address.zipCode',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: (contact: NContact.Entity) => {
        const current = contact.addresses?.find((a) => a.isCurrent);
        return current?.zip;
      },
    },
  },

  // Document Specifics
  {
    label: 'Expiration Date',
    name: 'ocrResult.driverLicenseFront.expirationDate',
    component: TextInputField,
    disabled: true,
    children: formatDate(stipReview?.ocrResult?.driverLicenseFront?.expirationDate, { format: dateFormat }, ocrDateFormat),
    compareWith: {
      contactPath: 'driverLicenseExpireDate',
    },
  },
  {
    label: 'Issue Date',
    name: 'ocrResult.driverLicenseFront.issueDate',
    component: TextInputField,
    disabled: true,
    children: formatDate(stipReview?.ocrResult?.driverLicenseFront?.issueDate, { format: dateFormat }, ocrDateFormat),
    compareWith: {
      contactPath: 'driverLicenseIssuesOnDate',
    },
  },
  {
    label: 'Issuing State',
    name: 'ocrResult.driverLicenseFront.issuingState',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactPath: 'driverLicenseState',
    },
  },

  // Physical Attributes
  {
    label: 'Sex',
    name: 'ocrResult.driverLicenseFront.sex',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Height',
    name: 'ocrResult.driverLicenseFront.height',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Weight',
    name: 'ocrResult.driverLicenseFront.weight',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Eye Color',
    name: 'ocrResult.driverLicenseFront.eyeColor',
    component: TextInputField,
    disabled: true,
  },

  // License Class & Restrictions
  {
    label: 'Class',
    name: 'ocrResult.driverLicenseFront.class',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Restrictions',
    name: 'ocrResult.driverLicenseFront.restriction',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Endorsements',
    name: 'ocrResult.driverLicenseFront.endorsements',
    component: TextInputField,
    disabled: true,
  },
];

const dlFrontCbConfigV2 = (stipReview?: IStip) => [
  {
    label: 'License Number',
    name: 'ocrResult.driverLicenseFront.licenseNumber',
    component: TextInputField,
    disabled: true,
    compareWith: {
      coApplicantPath: 'driverLicenseNumber',
    },
  },
  {
    label: 'Full Name',
    name: 'ocrResult.driverLicenseFront.fullName',
    component: TextInputField,
    disabled: true,
    compareWith: {
      coApplicantResolver: getContactFullName,
    },
  },
  {
    label: 'Date of Birth',
    name: 'ocrResult.driverLicenseFront.dob',
    component: TextInputField,
    disabled: true,
    children: formatDate(stipReview?.ocrResult?.driverLicenseFront?.dob, { format: dateFormat }, ocrDateFormat),
    compareWith: {
      coApplicantPath: 'dateOfBirth',
    },
  },

  // Address Section
  {
    label: 'Street Address',
    name: 'ocrResult.driverLicenseFront.address.street',
    component: TextInputField,
    disabled: true,
    compareWith: {
      coApplicantResolver: getCurrentContactStreet,
    },
  },
  {
    label: 'City',
    name: 'ocrResult.driverLicenseFront.address.city',
    component: TextInputField,
    disabled: true,
    compareWith: {
      coApplicantResolver: (coApplicant: ICoApplicant) => {
        const current = coApplicant.addresses?.find((a) => a.isCurrent);
        return current?.city;
      },
    },
  },
  {
    label: 'State',
    name: 'ocrResult.driverLicenseFront.address.state',
    component: TextInputField,
    disabled: true,
    compareWith: {
      coApplicantResolver: (coApplicant: ICoApplicant) => {
        const current = coApplicant.addresses?.find((a) => a.isCurrent);
        return current?.state;
      },
    },
  },
  {
    label: 'Zip Code',
    name: 'ocrResult.driverLicenseFront.address.zipCode',
    component: TextInputField,
    disabled: true,
    compareWith: {
      coApplicantResolver: (coApplicant: ICoApplicant) => {
        const current = coApplicant.addresses?.find((a) => a.isCurrent);
        return current?.zip;
      },
    },
  },

  // Document Specifics
  {
    label: 'Expiration Date',
    name: 'ocrResult.driverLicenseFront.expirationDate',
    component: TextInputField,
    disabled: true,
    children: formatDate(stipReview?.ocrResult?.driverLicenseFront?.expirationDate, { format: dateFormat }, ocrDateFormat),
    compareWith: {
      coApplicantPath: 'driverLicenseExpireDate',
    },
  },
  {
    label: 'Issue Date',
    name: 'ocrResult.driverLicenseFront.issueDate',
    component: TextInputField,
    disabled: true,
    children: formatDate(stipReview?.ocrResult?.driverLicenseFront?.issueDate, { format: dateFormat }, ocrDateFormat),
    compareWith: {
      coApplicantPath: 'driverLicenseIssuesOnDate',
    },
  },
  {
    label: 'Issuing State',
    name: 'ocrResult.driverLicenseFront.issuingState',
    component: TextInputField,
    disabled: true,
    compareWith: {
      coApplicantPath: 'driverLicenseState',
    },
  },

  // Physical Attributes
  {
    label: 'Sex',
    name: 'ocrResult.driverLicenseFront.sex',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Height',
    name: 'ocrResult.driverLicenseFront.height',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Weight',
    name: 'ocrResult.driverLicenseFront.weight',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Eye Color',
    name: 'ocrResult.driverLicenseFront.eyeColor',
    component: TextInputField,
    disabled: true,
  },

  // License Class & Restrictions
  {
    label: 'Class',
    name: 'ocrResult.driverLicenseFront.class',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Restrictions',
    name: 'ocrResult.driverLicenseFront.restriction',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Endorsements',
    name: 'ocrResult.driverLicenseFront.endorsements',
    component: TextInputField,
    disabled: true,
  },
];

const dlBackConfig = () => [
  {
    label: 'Issuing State',
    name: 'ocrResult.driverLicenseBack.issuingState',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactPath: 'driverLicenseState',
    },
  },
  {
    label: 'Endorsements',
    name: 'ocrResult.driverLicenseBack.endorsement',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Doc Number',
    name: 'ocrResult.driverLicenseBack.docNumber',
    component: TextInputField,
    disabled: true,
  },
];

const dlBackCbConfig = () => [
  {
    label: 'Issuing State',
    name: 'ocrResult.driverLicenseBack.issuingState',
    component: TextInputField,
    disabled: true,
    compareWith: {
      coApplicantPath: 'driverLicenseState',
    },
  },
  {
    label: 'Endorsements',
    name: 'ocrResult.driverLicenseBack.endorsement',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Doc Number',
    name: 'ocrResult.driverLicenseBack.docNumber',
    component: TextInputField,
    disabled: true,
  },
];

const odoMeterConfigV2 = () => [
  {
    label: 'Odometer',
    name: 'ocrResult.odometer.odometerReading',
    component: TextInputField,
    disabled: true,
    compareWith: {
      assetPath: 'mileage',
    },
  },
];

const proofOfIncomeConfig = () => [
  // Employee Information
  {
    label: 'Employee Name',
    name: 'ocrResult.proofOfIncome.employeeName',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: getContactFullName,
    },
  },
  {
    label: 'Employee Street',
    name: 'ocrResult.proofOfIncome.employeeAddress.street',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: getCurrentContactStreet,
    },
  },
  {
    label: 'Employee City',
    name: 'ocrResult.proofOfIncome.employeeAddress.city',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: (contact: NContact.Entity) => {
        const current = contact.addresses?.find((a) => a.isCurrent);
        return current?.city;
      },
    },
  },
  {
    label: 'Employee State',
    name: 'ocrResult.proofOfIncome.employeeAddress.state',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: (contact: NContact.Entity) => {
        const current = contact.addresses?.find((a) => a.isCurrent);
        return current?.state;
      },
    },
  },
  {
    label: 'Employee Zip Code',
    name: 'ocrResult.proofOfIncome.employeeAddress.zipCode',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: (contact: NContact.Entity) => {
        const current = contact.addresses?.find((a) => a.isCurrent);
        return current?.zip;
      },
    },
  },

  // Employer Information
  {
    label: 'Employer Name',
    name: 'ocrResult.proofOfIncome.employerName',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: getEmployeeDetails('companyName'),
    },
  },
  {
    label: 'Employer ID Number (EIN)',
    name: 'ocrResult.proofOfIncome.employerIDNumber',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Employer Street',
    name: 'ocrResult.proofOfIncome.employerAddress.street',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: getEmployeeDetails('streetLine1'),
    },
  },
  {
    label: 'Employer City',
    name: 'ocrResult.proofOfIncome.employerAddress.city',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: getEmployeeDetails('city'),
    },
  },
  {
    label: 'Employer State',
    name: 'ocrResult.proofOfIncome.employerAddress.state',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: getEmployeeDetails('state'),
    },
  },
  {
    label: 'Employer Zip Code',
    name: 'ocrResult.proofOfIncome.employerAddress.zipCode',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: getEmployeeDetails('zip'),
    },
  },

  // Tax and Wage Details
  {
    label: 'Tax Year',
    name: 'ocrResult.proofOfIncome.taxYear',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Form Type',
    name: 'ocrResult.proofOfIncome.formType',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Wages, Tips, Other Compensation',
    name: 'ocrResult.proofOfIncome.wagesTipsOtherCompensation',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Federal Income Tax Withheld',
    name: 'ocrResult.proofOfIncome.federalIncomeTaxWithheld',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Social Security Wages',
    name: 'ocrResult.proofOfIncome.socialSecurityWages',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Social Security Tax Withheld',
    name: 'ocrResult.proofOfIncome.socialSecurityTaxWithheld',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Medicare Wages',
    name: 'ocrResult.proofOfIncome.medicareWages',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Medicare Tax Withheld',
    name: 'ocrResult.proofOfIncome.medicareTaxWithheld',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'State Wages',
    name: 'ocrResult.proofOfIncome.stateWages',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'State Tax Withheld',
    name: 'ocrResult.proofOfIncome.stateTaxWithheld',
    component: TextInputField,
    disabled: true,
  },
];

const proofOfResidencyConfig = () => [
  // Personal & Address Information
  {
    label: 'Full Name',
    name: 'ocrResult.proofOfResidency.fullName',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: getContactFullName,
    },
  },
  {
    label: 'Street Address',
    name: 'ocrResult.proofOfResidency.address.street',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: getCurrentContactStreet,
    },
  },
  {
    label: 'City',
    name: 'ocrResult.proofOfResidency.address.city',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: (contact: NContact.Entity) => {
        const current = contact.addresses?.find((a) => a.isCurrent);
        return current?.city;
      },
    },
  },
  {
    label: 'State',
    name: 'ocrResult.proofOfResidency.address.state',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: (contact: NContact.Entity) => {
        const current = contact.addresses?.find((a) => a.isCurrent);
        return current?.state;
      },
    },
  },
  {
    label: 'Zip Code',
    name: 'ocrResult.proofOfResidency.address.zipCode',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: (contact: NContact.Entity) => {
        const current = contact.addresses?.find((a) => a.isCurrent);
        return current?.zip;
      },
    },
  },

  // Document & Issuer Details
  {
    label: 'Issuer / Company',
    name: 'ocrResult.proofOfResidency.issuer',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Issuer Type',
    name: 'ocrResult.proofOfResidency.issuerType',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Service Type',
    name: 'ocrResult.proofOfResidency.serviceType',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Account Number',
    name: 'ocrResult.proofOfResidency.accountNumber',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Reference Number',
    name: 'ocrResult.proofOfResidency.referenceNumber',
    component: TextInputField,
    disabled: true,
  },

  // Dates & Billing
  {
    label: 'Statement Date',
    name: 'ocrResult.proofOfResidency.statementDate',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Issue Date',
    name: 'ocrResult.proofOfResidency.issueDate',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Billing Period Start',
    name: 'ocrResult.proofOfResidency.billingPeriodStart',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Billing Period End',
    name: 'ocrResult.proofOfResidency.billingPeriodEnd',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Amount Due',
    name: 'ocrResult.proofOfResidency.amountDue',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Payment Due Date',
    name: 'ocrResult.proofOfResidency.paymentDueDate',
    component: TextInputField,
    disabled: true,
  },

  // Contact Info
  {
    label: 'Contact Number',
    name: 'ocrResult.proofOfResidency.contactNumber',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Email',
    name: 'ocrResult.proofOfResidency.email',
    component: TextInputField,
    disabled: true,
  },
];

export const bookoutConfig = (stipReview?: IStip) => [
      {
        label: 'Bookout',
        component: Typography,
        variant: 'subtitle1',
      },
      {
        label: 'Bookout Date',
        name: 'bokBookoutDate',
        component: SelectDateField,
      },

      {
        label: 'LC',
        name: 'lc',
        component: Typography,
        children: stipReview?.lc,
      },
      {
        label: 'LC Approval Date',
        name: 'lcApprovalDate',
        component: Typography,
        children: formatDate(stipReview?.lcApprovalDate, { format: dateFormatWithTime }),
      },
      {
        label: 'LC Status',
        name: '',
        component: Typography,
        children: stipReview?.lcReview && getStatus(stipReview.reviewStatus),
        color: muiTheme.palette.success.main,
      },
      {
        label: 'LC Note',
        name: 'lcNote',
        component: Typography,
        children: stipReview?.lcNote,
      },
      {
        label: 'LP',
        name: 'lp',
        component: Typography,
        children: stipReview?.lp,
      },
      {
        label: 'LP Approval Date',
        name: 'lpApprovalDate',
        component: Typography,
        children: formatDate(stipReview?.lpApprovalDate, { format: dateFormatWithTime }),
      },
      {
        label: 'LP Status',
        name: '',
        component: Typography,
        children: stipReview?.lpReview && getStatus(stipReview.reviewStatus),
      },
      {
        label: 'LP Note',
        name: 'lpNote',
        component: Typography,
        children: stipReview?.lpNote,
      },
      {
        label: 'Bookout Valuation',
        name: 'bookOutValuation',
        component: SelectAutocompleteField,
        options: VALUATION_TYPE_OPTIONS,
      },
      {
        label: 'Lender Valuation',
        name: 'boklenderValuation',
        component: Typography,
        children: stipReview?.boklenderValuation,
      },
      {
        label: 'Bookout Amount',
        name: 'bokAmount',
        prefix: '$',
        component: NumberInputField,
      },
      {
        label: 'VIN Matches?',
        checkboxName: 'bokVinMatchs',
        component: Typography,
        children: stipReview?.vinNumber?.toUpperCase(),
        name: 'keyBookOutVinMatch',
      },
      {
        label: 'Mileage',
        checkboxName: 'bokMileageMatchs',
        component: Typography,
        children: stipReview?.mileage,
        name: 'keyBookMileage',
      },
      {
        label: 'KBB',
        name: 'bokKBB',
        component: Typography,
        children: stipReview?.bokKBB,
      },
      {
        label: 'JD Power',
        name: 'bokNADA',
        component: Typography,
        children: stipReview?.bokNADA,
      },
      {
        label: 'MSRP',
        name: 'bokMSRP',
        component: Typography,
        children: stipReview?.bokMSRP,
      },
      {
        label: 'Region/State is ContactCurrentState',
        checkboxName: 'bokStateMatchs',
        component: Typography,
        children: '',
        name: 'keyNameStateMatch',
      },
      {
        label: 'Double-Adds',
        checkboxName: 'bokDoubleAdds',
        component: Typography,
        children: '',
        name: 'keyBokDoubleAdds',
      },
    ];

export const regConfigV2 = () => [
  // Vehicle Specifics
  {
    label: 'Plate Number',
    name: 'ocrResult.registration.plateNumber',
    component: TextInputField,
    disabled: true,
    compareWith: {
      assetPath: 'assetRegistration.plateNumber',
    },
  },
  {
    label: 'VIN',
    name: 'ocrResult.registration.vin',
    component: TextInputField,
    disabled: true,
    compareWith: {
      assetPath: 'vin',
    },
  },
  {
    label: 'Year',
    name: 'ocrResult.registration.year',
    component: TextInputField,
    disabled: true,
    compareWith: {
      assetPath: 'year',
    },
  },
  {
    label: 'Make',
    name: 'ocrResult.registration.make',
    component: TextInputField,
    disabled: true,
    compareWith: {
      assetPath: 'make',
    },
  },
  {
    label: 'Model',
    name: 'ocrResult.registration.model',
    component: TextInputField,
    disabled: true,
    compareWith: {
      assetPath: 'model',
    },
  },
  // Registration & Compliance
  {
    label: 'Registration Number',
    name: 'ocrResult.registration.registrationNumber',
    component: TextInputField,
    disabled: true,
    compareWith: {
      assetPath: 'assetRegistration.number',
    },
  },
  {
    label: 'Expiry Date',
    name: 'ocrResult.registration.expiryDate',
    component: TextInputField,
    disabled: true,
    compareWith: {
      assetPath: 'assetRegistration.expirationDate',
    },
  },
  {
    label: 'Issue Date',
    name: 'ocrResult.registration.issueDate',
    component: TextInputField,
    disabled: true,
    compareWith: {
      assetPath: 'assetRegistration.issueDate',
    },
  },
  {
    label: 'Owner Name',
    name: 'ocrResult.registration.ownerName',
    component: TextInputField,
    disabled: true,
    compareWith: {
      assetPath: 'assetRegistration.primaryOwner',
    },
  },
  {
    label: 'Street Address',
    name: 'ocrResult.registration.address.street',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: getCurrentContactStreet,
    },
  },
  {
    label: 'City',
    name: 'ocrResult.registration.address.city',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: (contact: NContact.Entity) => {
        const current = contact.addresses?.find((a) => a.isCurrent);
        return current?.city;
      },
    },
  },
  {
    label: 'State',
    name: 'ocrResult.registration.address.state',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: (contact: NContact.Entity) => {
        const current = contact.addresses?.find((a) => a.isCurrent);
        return current?.state;
      },
    },
  },
  {
    label: 'Zip Code',
    name: 'ocrResult.registration.address.zipCode',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: (contact: NContact.Entity) => {
        const current = contact.addresses?.find((a) => a.isCurrent);
        return current?.zip;
      },
    },
  },
  {
    label: 'Amount Paid',
    name: 'ocrResult.registration.amountPaid',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'County',
    name: 'ocrResult.registration.county',
    component: TextInputField,
    disabled: true,
  },
];

const insuranceConfigV2 = () => [
  // Policy Details
  {
    label: 'Insurer Name',
    name: 'ocrResult.insurance.insurerName',
    component: TextInputField,
    disabled: true,
    compareWith: {
      assetPath: 'assetInsurance.company',
    },
  },
  {
    label: 'Policy Number',
    name: 'ocrResult.insurance.policyNumber',
    component: TextInputField,
    disabled: true,
    compareWith: {
      assetPath: 'assetInsurance.policyNumber',
    },
  },
  {
    label: 'Effective Date',
    name: 'ocrResult.insurance.effectiveDate',
    component: TextInputField,
    disabled: true,
    compareWith: {
      assetPath: 'assetInsurance.effectiveDate',
    },
  },
  {
    label: 'Expiration Date',
    name: 'ocrResult.insurance.expirationDate',
    component: TextInputField,
    disabled: true,
    compareWith: {
      assetPath: 'assetInsurance.expirationDate',
    },
  },
  {
    label: 'NAIC Number',
    name: 'ocrResult.insurance.naicNumber',
    component: TextInputField,
    disabled: true,
  },
  {
    label: 'Vehicle VIN',
    name: 'ocrResult.insurance.vin',
    component: TextInputField,
    disabled: true,
    compareWith: {
      assetPath: 'vin',
    },
  },
  {
    label: 'Year',
    name: 'ocrResult.insurance.year',
    component: TextInputField,
    disabled: true,
    compareWith: {
      assetPath: 'year',
    },
  },
  {
    label: 'Make',
    name: 'ocrResult.insurance.make',
    component: TextInputField,
    disabled: true,
    compareWith: {
      assetPath: 'make',
    },
  },
  {
    label: 'Model',
    name: 'ocrResult.insurance.model',
    component: TextInputField,
    disabled: true,
    compareWith: {
      assetPath: 'model',
    },
  },

  // Owner & Contact Section
  {
    label: 'Owner Name',
    name: 'ocrResult.insurance.ownerName',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: getContactFullName,
    },
  },
  {
    label: 'Issuer Contact',
    name: 'ocrResult.insurance.issuerContact',
    component: TextInputField,
    disabled: true,
    compareWith: {
      assetPath: 'assetInsurance.phoneNumber',
    },
  },

  // Address Section
  {
    label: 'Street Address',
    name: 'ocrResult.insurance.address.street',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: getCurrentContactStreet,
    },
  },
  {
    label: 'City',
    name: 'ocrResult.insurance.address.city',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: (contact: NContact.Entity) => {
        const current = contact.addresses?.find((a) => a.isCurrent);
        return current?.city;
      },
    },
  },
  {
    label: 'State',
    name: 'ocrResult.insurance.address.state',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: (contact: NContact.Entity) => {
        const current = contact.addresses?.find((a) => a.isCurrent);
        return current?.state;
      },
    },
  },
  {
    label: 'Zip Code',
    name: 'ocrResult.insurance.address.zipCode',
    component: TextInputField,
    disabled: true,
    compareWith: {
      contactResolver: (contact: NContact.Entity) => {
        const current = contact.addresses?.find((a) => a.isCurrent);
        return current?.zip;
      },
    },
  },
];
